import { useEffect, useState, useCallback } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Eye, EyeOff, Copy, Pencil, Trash2, ExternalLink, KeyRound, Lock } from "lucide-react";
import { toast } from "sonner";
import { PLATFORMS, PLATFORM_MAP, PlatformIcon } from "@/components/PlatformIcons";

export default function VaultPage() {
  const { activeBusiness } = useBusiness();
  const [creds, setCreds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState(null); // null = all
  const [editing, setEditing] = useState(null); // credential being edited
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    if (!activeBusiness) return;
    setLoading(true);
    try {
      const { data } = await apiAdmin.get(`/vault?business_id=${activeBusiness.id}`);
      setCreds(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [activeBusiness]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiAdmin.delete(`/vault/${confirmDelete.id}`);
      toast.success("Credential deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Filter + group
  const filtered = creds.filter((c) => {
    if (platformFilter && c.platform !== platformFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.username.toLowerCase().includes(q) ||
      (c.label || "").toLowerCase().includes(q) ||
      c.platform.toLowerCase().includes(q)
    );
  });

  // Counts per platform for the filter chips
  const counts = creds.reduce((acc, c) => {
    acc[c.platform] = (acc[c.platform] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="vault-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Security</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Password Vault
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Securely store social media and platform logins for{" "}
            <span className="text-slate-700 font-medium">{activeBusiness?.name}</span>.{" "}
            All passwords are encrypted at rest with AES.
          </p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="vault-new-button"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New credential
        </Button>
      </div>

      {/* Filter bar */}
      <div className="rounded-2xl bg-white border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by platform, username or label"
            className="pl-9 h-9"
            data-testid="vault-search-input"
          />
        </div>
        <button
          onClick={() => setPlatformFilter(null)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            platformFilter === null
              ? "border-slate-900 bg-slate-900 text-white"
              : "border-slate-200 bg-white text-slate-500 hover:text-slate-900"
          }`}
          data-testid="vault-filter-all"
        >
          All · {creds.length}
        </button>
        {PLATFORMS.filter((p) => counts[p.id]).map((p) => (
          <button
            key={p.id}
            onClick={() => setPlatformFilter(platformFilter === p.id ? null : p.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors inline-flex items-center gap-1.5 ${
              platformFilter === p.id
                ? "border-blue-500 bg-blue-50 text-blue-700"
                : "border-slate-200 bg-white text-slate-500 hover:text-slate-900"
            }`}
            data-testid={`vault-filter-${p.id}`}
          >
            <PlatformIcon platform={p.id} size={14} />
            {p.label} · {counts[p.id]}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {!loading && creds.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <Lock className="h-10 w-10 mx-auto text-slate-400 mb-3" strokeWidth={1.25} />
          <div className="font-heading text-lg font-semibold text-slate-900">Your vault is empty</div>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Save your Instagram, Facebook, TikTok, YouTube and other platform logins so your team has them in one place — encrypted.
          </p>
          <Button
            onClick={() => setCreating(true)}
            className="mt-5 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="vault-empty-cta"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add your first credential
          </Button>
        </div>
      )}

      {filtered.length === 0 && creds.length > 0 && (
        <div className="text-sm text-slate-500 text-center py-8">
          No credentials match your filter.
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <CredentialCard
            key={c.id}
            credential={c}
            onEdit={() => setEditing(c)}
            onDelete={() => setConfirmDelete(c)}
          />
        ))}
      </div>

      {/* New / Edit dialog */}
      {(creating || editing) && (
        <CredentialEditor
          open={creating || !!editing}
          credential={editing}
          business={activeBusiness}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="vault-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this credential?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The encrypted password will be permanently removed
              from your vault.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={handleDelete}
              data-testid="vault-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ----- Card ----- */
function CredentialCard({ credential: c, onEdit, onDelete }) {
  const [revealed, setRevealed] = useState(false);
  const [password, setPassword] = useState("");
  const platform = PLATFORM_MAP[c.platform] || PLATFORM_MAP.other;

  const togglePassword = async () => {
    if (revealed) { setRevealed(false); return; }
    try {
      const { data } = await apiAdmin.get(`/vault/${c.id}/reveal`);
      setPassword(data.password || "");
      setRevealed(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Could not copy");
    }
  };

  const copyPassword = async () => {
    try {
      const { data } = await apiAdmin.get(`/vault/${c.id}/reveal`);
      copy(data.password || "", "Password");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col gap-3 hover:border-slate-300 transition-colors"
      data-testid={`vault-card-${c.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="h-10 w-10 rounded-xl grid place-items-center shrink-0"
            style={{ background: `${platform.color}15`, color: platform.color }}
          >
            <PlatformIcon platform={c.platform} size={20} />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 truncate">
              {c.label || platform.label}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">
              {platform.label}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {c.url && (
            <button
              onClick={() => window.open(c.url, "_blank", "noopener")}
              className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-500"
              title="Open URL"
              data-testid={`vault-${c.id}-url`}
            >
              <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
            </button>
          )}
          <button
            onClick={onEdit}
            className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-100 text-slate-500"
            title="Edit"
            data-testid={`vault-${c.id}-edit`}
          >
            <Pencil className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <button
            onClick={onDelete}
            className="h-8 w-8 grid place-items-center rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600"
            title="Delete"
            data-testid={`vault-${c.id}-delete`}
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Username */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Username</div>
        <div className="flex items-center gap-2">
          <div className="text-sm text-slate-900 font-mono truncate flex-1">{c.username}</div>
          <button
            onClick={() => copy(c.username, "Username")}
            className="h-7 w-7 grid place-items-center rounded-md hover:bg-slate-100 text-slate-500"
            title="Copy username"
            data-testid={`vault-${c.id}-copy-user`}
          >
            <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Password */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Password</div>
        <div className="flex items-center gap-2">
          <div className="text-sm text-slate-900 font-mono truncate flex-1 select-none">
            {revealed ? password : "••••••••••••"}
          </div>
          <button
            onClick={togglePassword}
            className="h-7 w-7 grid place-items-center rounded-md hover:bg-slate-100 text-slate-500"
            title={revealed ? "Hide" : "Show"}
            data-testid={`vault-${c.id}-reveal`}
          >
            {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={copyPassword}
            className="h-7 w-7 grid place-items-center rounded-md hover:bg-slate-100 text-slate-500"
            title="Copy password"
            data-testid={`vault-${c.id}-copy-pass`}
          >
            <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {c.notes && (
        <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2.5 line-clamp-3">
          {c.notes}
        </div>
      )}
    </div>
  );
}

/* ----- Editor dialog ----- */
function CredentialEditor({ open, credential, business, onClose, onSaved }) {
  const [platform, setPlatform] = useState(credential?.platform || "instagram");
  const [label, setLabel] = useState(credential?.label || "");
  const [username, setUsername] = useState(credential?.username || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [url, setUrl] = useState(credential?.url || "");
  const [notes, setNotes] = useState(credential?.notes || "");
  const [saving, setSaving] = useState(false);

  const isEdit = !!credential;

  const submit = async () => {
    if (!username.trim()) return toast.error("Username is required");
    if (!isEdit && !password) return toast.error("Password is required");
    setSaving(true);
    try {
      if (isEdit) {
        const body = { platform, label, username, url, notes };
        if (password) body.password = password;
        await apiAdmin.patch(`/vault/${credential.id}`, body);
        toast.success("Credential updated");
      } else {
        await apiAdmin.post("/vault", {
          business_id: business.id,
          platform, label, username, password, url, notes,
        });
        toast.success("Credential saved (encrypted)");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="vault-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-blue-400" />
            {isEdit ? "Edit credential" : "New credential"}
          </DialogTitle>
          <DialogDescription>
            Pick a platform and enter the login info. Passwords are encrypted with AES on save.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Platform picker */}
          <div>
            <Label className="text-xs text-slate-500">Platform</Label>
            <div className="grid grid-cols-5 gap-2 mt-1.5">
              {PLATFORMS.map((p) => {
                const active = platform === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPlatform(p.id)}
                    className={`rounded-xl p-2.5 flex flex-col items-center gap-1 border transition-all ${
                      active
                        ? "bg-blue-500/10 border-blue-500/40"
                        : "bg-white border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`vault-platform-${p.id}`}
                    title={p.label}
                  >
                    <PlatformIcon platform={p.id} size={20} />
                    <span className="text-[10px] text-slate-400 truncate w-full text-center">{p.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-500">Label (optional)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. IG main account"
              className="mt-1.5"
              data-testid="vault-editor-label"
            />
          </div>

          <div>
            <Label className="text-xs text-slate-500">Username / Email</Label>
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="@username or email@example.com"
              className="mt-1.5"
              autoComplete="off"
              data-testid="vault-editor-username"
            />
          </div>

          <div>
            <Label className="text-xs text-slate-500">
              Password {isEdit && <span className="text-slate-600">(leave empty to keep current)</span>}
            </Label>
            <div className="relative mt-1.5">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isEdit ? "••••••••" : "Strong password"}
                autoComplete="new-password"
                className="pr-10"
                data-testid="vault-editor-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-slate-900"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-500">URL (optional)</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://instagram.com/yourhandle"
              className="mt-1.5"
              data-testid="vault-editor-url"
            />
          </div>

          <div>
            <Label className="text-xs text-slate-500">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="2FA backup codes, recovery email, etc."
              rows={2}
              className="mt-1.5"
              data-testid="vault-editor-notes"
            />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="vault-editor-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="vault-editor-save"
          >
            <Lock className="h-3.5 w-3.5 mr-1.5" />
            {saving ? "Encrypting…" : isEdit ? "Save changes" : "Save & encrypt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
